import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import { useGetVQAParticipantsList } from "../../custom-hooks/queries";
import Pagination from "../../components/CustomPagination";
import SkeletonLoader from "../../components/SkeletonLoader";
import { dateFormat } from "../../utils/helperMethods";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import DropdownMenu from "../../components/Dropdown";
import StatusIndicator from "../../components/StatusIndicator";

const ManageParticipantsList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const searchQuery = useSelector(
    (state) => state.providersFilters.searchTextByPage["myDocuments"]
  );
  const menuItems = [{ label: "View", href: "#" }];

  const debouncedSearch = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({ pageKey: "myDocuments", searchText: search })
      );
    }, 300),
    [dispatch]
  );

  // Retrieve the participants list using your custom hook
  const { data: providers, isLoading, error } = useGetVQAParticipantsList(id);

  const handleEventClick = (item) => {
    if (item.join_vqa_request_id) {
      navigate(`/JoinRequest/${item.join_vqa_request_id}/Details`);
    }
  };

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
      {/* Header */}
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-5">
        <h1 className="font-semibold">Participated Sponsor(s) List</h1>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <SkeletonLoader />
            ) : providers && providers.items && providers.items.length > 0 ? (
              <div className="overflow-x-auto lg:overflow-x-visible">
                <table className="min-w-full divide-y divide-gray-300 table-fixed ml-[-27px] mr-[-27px]">
                  <thead className="bg-gray-100  ">
                    <tr>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Sponsor
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Request Date
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Vendor
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        VQA Start Date
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Agreed Draft Report Date
                      </th>
                      <th className="w-1/5 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {providers.items.map((item) => (
                      <tr key={item.join_vqa_request_id}>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.sponsor_name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {dateFormat(item.join_vqa_request_created_on)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <StatusIndicator
                            status={item.join_vqa_request_status}
                          />
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.provider_name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.assessment_start_date
                            ? dateFormat(item.assessment_start_date)
                            : "N/A"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.agreed_initial_draft_date
                            ? dateFormat(item.agreed_initial_draft_date)
                            : "N/A"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 text-right">
                          <DropdownMenu
                            menuItems={menuItems}
                            navigation={`/JoinRequest/${item.join_vqa_request_id}/Details`}
                            icon={true}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // Custom "No Record Found" Design
              <div
                className="flex flex-col items-center justify-center text-center space-y-2 min-h-96"
                style={{ marginTop: "100px" }}
              >
                <span className="p-3 rounded-full bg-custom-link-bg-color">
                  <MagnifyingGlassIcon
                    width={20}
                    height={20}
                    style={{ color: "white" }}
                  />
                </span>
                <p className="text-lg text-gray-700 font-semibold">
                  No Assessments Requested
                </p>
                <p className="text-gray-500">
                  Your search did not match any Assessments. Please try again.
                </p>
              </div>
            )}
            {/* Pagination */}
            <div className="mt-5">
              <Pagination
                totalPages={providers?.total_pages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageParticipantsList;
